import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { AdminSidebar } from "@/components/sidebars";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-MX") : "—");

const STATUS_BADGE = {
  new: { bg: "#E7EBF5", color: "#163680", label: "Nuevo" },
  qualified: { bg: "#FEF3C7", color: "#92400E", label: "Calificado" },
  hot: { bg: "#FEE2E2", color: "#B91C1C", label: "🔥 Hot" },
  converted: { bg: "#DCFCE7", color: "#15803D", label: "Convertido" },
};

function StatusPill({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.new;
  return (
    <span
      data-testid={`wa-status-${status}`}
      style={{
        background: s.bg,
        color: s.color,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {s.label}
    </span>
  );
}

function ConversationView({ convo, onReply }) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  if (!convo) {
    return (
      <div className="panel" style={{ minHeight: 400, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p className="text-muted">Selecciona una conversación a la izquierda</p>
      </div>
    );
  }

  const sendReply = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    try {
      await apiClient.post("/admin/whatsapp/reply", { phone: convo.phone, text: draft.trim() });
      toast.success("Mensaje enviado");
      setDraft("");
      onReply();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="panel" data-testid="wa-conversation-view">
      <header style={{ borderBottom: "1px solid #e7eaf2", paddingBottom: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, color: "#163680" }}>
          {convo.name || "Sin nombre"} <small style={{ color: "#6b7280", fontWeight: 400 }}>· +{convo.phone}</small>
        </h3>
        <small style={{ color: "#9ca3af" }}>
          <StatusPill status={convo.status} /> · Actualizado {fmtDate(convo.updated_at)}
        </small>
      </header>

      <div
        style={{
          maxHeight: 480,
          overflowY: "auto",
          padding: "8px 4px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
        data-testid="wa-messages"
      >
        {convo.messages?.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === "user" ? "flex-start" : "flex-end",
              maxWidth: "75%",
              background: m.role === "user" ? "#f1f5f9" : m.role === "admin" ? "#fef3c7" : "#163680",
              color: m.role === "user" ? "#111827" : m.role === "admin" ? "#92400E" : "white",
              padding: "8px 12px",
              borderRadius: 12,
              fontSize: 14,
              whiteSpace: "pre-wrap",
            }}
            data-testid={`wa-msg-${i}`}
          >
            {m.role === "admin" && <small style={{ display: "block", fontSize: 10, opacity: 0.7 }}>👤 Admin</small>}
            {m.role === "bot" && <small style={{ display: "block", fontSize: 10, opacity: 0.7 }}>🤖 Ángel</small>}
            {m.text}
            <small style={{ display: "block", fontSize: 10, opacity: 0.6, marginTop: 4 }}>{fmtDate(m.ts)}</small>
          </div>
        ))}
      </div>

      <form onSubmit={sendReply} style={{ marginTop: 14, display: "flex", gap: 8 }}>
        <input
          type="text"
          placeholder="Escribe un mensaje manual al lead…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={sending}
          style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid #e7eaf2" }}
          data-testid="wa-reply-input"
        />
        <button type="submit" className="btn btn-primary" disabled={sending || !draft.trim()} data-testid="wa-reply-send">
          {sending ? "Enviando…" : "Enviar"}
        </button>
      </form>
    </div>
  );
}

export default function AdminWhatsapp() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selectedPhone, setSelectedPhone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter ? `?status=${filter}` : "";
      const { data } = await apiClient.get(`/admin/whatsapp/leads${qs}`);
      setLeads(data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 10s when on this page
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const selected = leads.find((l) => l.phone === selectedPhone);

  return (
    <AppLayout sidebar={AdminSidebar}>
      <header className="app-header">
        <h1>
          <i className="fab fa-whatsapp" style={{ color: "#25D366", marginRight: 10 }} />
          Bot WhatsApp
        </h1>
        <p className="text-muted">
          Conversaciones generadas por el bot de IA. Los leads marcados como <strong>🔥 Hot</strong> ya recibieron link de pago.
        </p>
      </header>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["", "new", "qualified", "hot", "converted"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setFilter(s)}
            className={`btn ${filter === s ? "btn-primary" : "btn-secondary"}`}
            style={{ fontSize: 13, padding: "6px 14px" }}
            data-testid={`wa-filter-${s || "all"}`}
          >
            {s === "" ? "Todos" : STATUS_BADGE[s].label}
          </button>
        ))}
        <button type="button" onClick={load} className="btn btn-secondary" style={{ fontSize: 13, padding: "6px 14px", marginLeft: "auto" }} data-testid="wa-refresh">
          <i className="fas fa-sync" /> Actualizar
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 380px) 1fr", gap: 18 }}>
        <div className="panel" style={{ padding: 0, overflow: "hidden" }} data-testid="wa-leads-list">
          {loading ? (
            <p className="text-muted" style={{ padding: 14 }}>Cargando…</p>
          ) : leads.length === 0 ? (
            <p className="text-muted" style={{ padding: 14 }}>
              Sin leads todavía. Aparecerán cuando alguien escriba al número de WhatsApp configurado.
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {leads.map((l) => (
                <li
                  key={l.phone}
                  onClick={() => setSelectedPhone(l.phone)}
                  style={{
                    padding: 14,
                    borderBottom: "1px solid #e7eaf2",
                    cursor: "pointer",
                    background: selectedPhone === l.phone ? "#EEF3FB" : "white",
                  }}
                  data-testid={`wa-lead-${l.phone}`}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ color: "#163680" }}>{l.name || "Sin nombre"}</strong>
                    <StatusPill status={l.status} />
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>+{l.phone}</div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#374151",
                      marginTop: 6,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {l.messages?.[l.messages.length - 1]?.text || "—"}
                  </div>
                  <small style={{ color: "#9ca3af", fontSize: 11 }}>{fmtDate(l.updated_at)}</small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <ConversationView convo={selected} onReply={load} />
      </div>
    </AppLayout>
  );
}
